import { useCallback } from "react"
import { graphql, UseMutationConfig } from "react-relay"
import { type CustomMutationParams, useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import { useChGeneratePrescriptionMutation as useChGeneratePrescriptionMutationType } from "@/modules/patients/detail/prescription/ch/services/__generated__/useChGeneratePrescriptionMutation.graphql"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const generatePrescriptionDocumentMutation = graphql`
  mutation useChGeneratePrescriptionMutation($input: Ch_generatePrescriptionDocumentInput!) {
    ch_generatePrescriptionDocument(input: $input) {
      ch_Prescription {
        id
        fileId
        signedAt
        signedBy
        ...chPrescriptionFragment
      }
      errors {
        ... on Error {
          __typename
          message
        }
      }
    }
  }
`

type Config = Omit<UseMutationConfig<useChGeneratePrescriptionMutationType>, "variables"> & {
  id: string
  progressNoteId: string
}

// Until useMutationWithErrorDialog is refactored to deal with errors in a type safe way
// We have to resort to this alternative
const chElectronicPrescriptionHasHinEpdAuthenticationIssue = (errorTypeName: string) =>
  ["MissingHinEpdAuthHandle", "StaleHinEpdAuthHandle"].includes(errorTypeName)

const useChGeneratePrescriptionMutation = (params?: CustomMutationParams) => {
  const [commit, isFlight] = useMutationWithErrorDialog<useChGeneratePrescriptionMutationType>(
    generatePrescriptionDocumentMutation,
    params,
  )
  const patientById = usePatientParam()

  const commitHandler = useCallback(
    async (generateElectronicPrescriptionQrCode: boolean, config: Config) => {
      commit({
        variables: {
          input: { id: config.id, patientId: patientById, generateElectronicPrescriptionQrCode },
        },
        ...config,
      })
    },
    [commit, patientById],
  )

  return [commitHandler, isFlight] as const
}

export { chElectronicPrescriptionHasHinEpdAuthenticationIssue, useChGeneratePrescriptionMutation }
